/**
 * The app's single configured dayjs instance.
 *
 * dayjs ships a small core and moves everything else into plugins that have to
 * be registered before use, and registration is global. Doing it here, in the
 * module everything imports its dayjs from, means a file cannot accidentally get
 * an instance that is missing `.utc()` — which would not fail loudly, it would
 * quietly return a local-time value instead.
 *
 * Import from here, never from `dayjs` directly.
 */
import dayjs from "dayjs";
import arraySupport from "dayjs/plugin/arraySupport";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isoWeek from "dayjs/plugin/isoWeek";
import localizedFormat from "dayjs/plugin/localizedFormat";
import objectSupport from "dayjs/plugin/objectSupport";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import utc from "dayjs/plugin/utc";

// `.utc()`, `.local()` and `.utcOffset()`. The app reads UTC timestamps off the
// API and renders them local, so this one is load-bearing nearly everywhere.
dayjs.extend(utc);

// `.isSameOrBefore()`, which dayjs leaves out of core.
dayjs.extend(isSameOrBefore);

// The `L`/`LL`/`LLL` format tokens. Only `LLL` is in use, and only under the
// Spanish locale, where the whole point is that the locale decides the order of
// the parts.
dayjs.extend(localizedFormat);

// `startOf`/`endOf("isoWeek")`, for the Brain page's week intervals. ISO weeks
// start on Monday regardless of locale, which is the distinction the interval
// picker relies on — dayjs core's `"week"` is locale-dependent and would move
// the boundary under the Spanish locale.
dayjs.extend(isoWeek);

// `"quarter"` as a unit, for the same interval picker.
dayjs.extend(quarterOfYear);

// Construction from an array or an object — `dayjs([y, m, d, h, min])` and
// `dayjs({ year, month, date, hour })`. moment took both in core, and the event
// forms build their combined date-plus-time values that way. Both plugins keep
// moment's 0-indexed month, so those call sites are unchanged.
dayjs.extend(arraySupport);
dayjs.extend(objectSupport);

// Spanish is the app's other language (src/locales/es). Loading the locale
// registers it; it does not select it. `setLanguage` below does that, and
// `store/user/userActions` calls it from the user's language preference.
// Loading only the locales the app offers is the reason moment was 19% of the
// gzipped entry chunk: it bundles all 130-odd of them and used two.
import "dayjs/locale/es";

/**
 * Switches the active locale for every subsequent format call.
 *
 * The `ESP`/`ENG` values come from the user-preference record. The previous
 * moment call passed `"en_US"`, which is not a locale moment ships — it fell
 * back to `en` with a console warning, so `en` is what this preserves.
 */
export function setLanguage(language: "ESP" | "ENG" | string): void {
  dayjs.locale(language === "ESP" ? "es" : "en");
}

export default dayjs;
